/**
 * Brand directory search — same input-island contract as product search (R4A).
 * Uncontrolled draft + debounced filter; preserves brand-tile UX (not product dropdown).
 */
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import type { CatalogBrand } from "./brand-browser-v2.js";
import { matchBrands } from "./brand-normalization-v2.js";
import { brandMonogram, textFallbackLogoRecord } from "./brand-logo-registry-v2.js";
import { countryFlagForLabel } from "./country-browser-v2.js";
import { t } from "./store-v2-ui-copy.js";

const BRAND_FILTER_DEBOUNCE_MS = 150;

export function BrandBrowserV2({
  lang,
  brands,
  onOpenBrand,
  visualSearchSlot,
}: {
  lang: StoreV2Lang;
  brands: CatalogBrand[];
  onOpenBrand: (slug: string) => void;
  visualSearchSlot?: ReactNode;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const composingRef = useRef(false);
  const [filterQuery, setFilterQuery] = useState("");
  const [sort, setSort] = useState<"alphabetical" | "popular">("alphabetical");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const scheduleFilter = (raw: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setFilterQuery(raw), BRAND_FILTER_DEBOUNCE_MS);
  };

  const onInput = (e: FormEvent<HTMLInputElement>) => {
    if (composingRef.current) return;
    scheduleFilter(e.currentTarget.value);
  };

  const matched = useMemo(() => {
    if (!filterQuery.trim()) return { hits: brands.map((brand) => ({ brand })), didYouMean: null as CatalogBrand | null };
    const result = matchBrands(
      filterQuery,
      brands.map((brand) => ({ name: brand.name, slug: brand.slug })),
    );
    const bySlug = new Map(brands.map((brand) => [brand.slug, brand]));
    return {
      hits: result.hits
        .map((hit) => bySlug.get(hit.brand.slug))
        .filter((brand): brand is CatalogBrand => Boolean(brand))
        .map((brand) => ({ brand })),
      didYouMean: result.didYouMean ? bySlug.get(result.didYouMean.slug) ?? null : null,
    };
  }, [brands, filterQuery]);

  const visible = useMemo(() => {
    const list = matched.hits.map((hit) => hit.brand);
    return [...list].sort((a, b) =>
      sort === "popular"
        ? b.productCount - a.productCount || a.name.localeCompare(b.name)
        : a.name.localeCompare(b.name, "uk", { sensitivity: "base" }),
    );
  }, [matched.hits, sort]);

  return (
    <div className="sv2-body sv2-route-top" data-route-top="brands" data-testid="sv2-brand-browser">
      <div className="sv2-section-title">{t(lang, "brands")}</div>
      {brands.length === 0 ? (
        <p className="sv2-muted" data-testid="sv2-brands-loading">
          {lang === "en" ? "Loading brands…" : "Завантажуємо бренди…"}
        </p>
      ) : null}
      <div className="sv2-sticky-tools">
        <div className="sv2-search-row" data-testid="sv2-brand-search-row">
          <div className="sv2-search" data-brand-search-island="1">
            <input
              ref={inputRef}
              type="search"
              inputMode="search"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              defaultValue=""
              onCompositionStart={() => {
                composingRef.current = true;
                if (debounceRef.current) {
                  clearTimeout(debounceRef.current);
                  debounceRef.current = null;
                }
              }}
              onCompositionEnd={(e) => {
                composingRef.current = false;
                scheduleFilter(e.currentTarget.value);
              }}
              onInput={onInput}
              placeholder={t(lang, "brand_search")}
              aria-label={t(lang, "brand_search")}
            />
          </div>
          {visualSearchSlot}
        </div>
        <label className="sv2-sort">
          <span className="sv2-sort-label">{lang === "en" ? "Sort" : "Сортування"}</span>
          <select value={sort} onChange={(event) => setSort(event.target.value as typeof sort)}>
            <option value="alphabetical">{lang === "en" ? "Alphabetical" : "За алфавітом"}</option>
            <option value="popular">{lang === "en" ? "Popularity" : "За популярністю"}</option>
          </select>
        </label>
      </div>
      {matched.didYouMean ? (
        <button
          type="button"
          className="sv2-alert sv2-alert-ok"
          onClick={() => onOpenBrand(matched.didYouMean!.slug)}
        >
          {lang === "en"
            ? `Did you mean “${matched.didYouMean.name}”?`
            : `Можливо, ви шукали “${matched.didYouMean.name}”?`}
        </button>
      ) : null}
      <div className="sv2-brand-grid">
        {visible.map((brand) => {
          const logo = brand.logo ?? textFallbackLogoRecord(brand.slug, brand.name);
          const hasImage = Boolean(logo.local_asset_path);
          return (
            <button
              key={brand.brand_id}
              type="button"
              className="sv2-brand-tile"
              data-testid="sv2-brand-tile"
              data-brand-id={brand.brand_id}
              onClick={() => onOpenBrand(brand.slug)}
            >
              {hasImage ? (
                <img
                  src={logo.local_asset_path!}
                  alt=""
                  className="sv2-brand-logo"
                  width={80}
                  height={80}
                  loading="lazy"
                  decoding="async"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = "none";
                    const mono = e.currentTarget.nextElementSibling as HTMLElement | null;
                    if (mono) mono.hidden = false;
                  }}
                />
              ) : null}
              <span
                className="sv2-brand-mono"
                aria-label={t(lang, "brand_logo_fallback")}
                title={t(lang, "brand_logo_fallback")}
                hidden={hasImage}
              >
                {brandMonogram(brand.name)}
              </span>
              <strong>{brand.name}</strong>
              {brand.brand_country ? (
                <span className="sv2-muted sv2-brand-country-row">
                  {(() => {
                    const fl = countryFlagForLabel(brand.brand_country);
                    return (
                      <span className="sv2-country-flag" aria-hidden="true">
                        {fl.url ? (
                          <img
                            className="sv2-country-flag-img"
                            src={fl.url}
                            alt=""
                            width={18}
                            height={12}
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.display = "none";
                            }}
                          />
                        ) : null}
                        <span className="sv2-country-flag-emoji">{fl.emoji}</span>
                      </span>
                    );
                  })()}
                  {t(lang, "brand_country_label")}: {brand.brand_country}
                </span>
              ) : null}
              <span>
                {brand.productCount} {t(lang, "products")}
                {brand.activeAvailabilityCount > 0
                  ? ` · ${brand.activeAvailabilityCount} ${t(lang, "active_in_stock")}`
                  : ""}
              </span>
            </button>
          );
        })}
      </div>
      {visible.length === 0 ? <div className="sv2-muted">{t(lang, "nothing_found")}</div> : null}
    </div>
  );
}
